import { zodResolver } from "@hookform/resolvers/zod";
import { createId } from "@paralleldrive/cuid2";
import { ArrowLeftIcon, CheckIcon, SparkleIcon } from "@phosphor-icons/react";
import { defaultEducation, educationSchema, type Education } from "@reactive-resume/schema";
import {
  DateField,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { FreeTextInput } from "../shared/free-text-input";
import { SmartCombobox } from "../shared/smart-combobox";
import { formatEducationPeriod } from "./date-utils";
import { DEGREE_OPTIONS, STUDY_AREA_OPTIONS } from "./suggestions";

type Values = z.infer<typeof educationSchema>;
type Props = { item?: Education; onCancel: () => void; onSave: (value: Education) => void };

export const EducationForm = ({ item, onCancel, onSave }: Props) => {
  const form = useForm<Values>({
    resolver: zodResolver(educationSchema),
    mode: "onChange",
    defaultValues: { ...defaultEducation, id: createId(), ...item } as Values,
  });
  const start = (form.watch("startDate" as keyof Values) as string) ?? "";
  const end = (form.watch("endDate" as keyof Values) as string) ?? "";
  const present = Boolean(form.watch("isPresent" as keyof Values));
  const [dateError, setDateError] = useState(false);
  const onDateError = useCallback((value: boolean) => setDateError(value), []);
  useEffect(
    () => form.reset({ ...defaultEducation, id: createId(), ...item } as Values),
    [form, item],
  );
  useEffect(
    () =>
      form.setValue("date" as keyof Values, formatEducationPeriod(start, end, present) as never),
    [end, form, present, start],
  );

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-3.5 shadow-[0_10px_30px_rgba(30,27,75,0.05)] sm:p-4">
      <header className="mb-4 flex items-start justify-between border-b border-slate-100 pb-3">
        <div>
          <h3 className="text-sm font-bold text-indigo-950">
            {item ? "Modifier la formation" : "Nouvelle formation"}
          </h3>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Sélectionnez une suggestion ou conservez votre propre saisie.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-8 items-center gap-1 rounded-lg px-2 text-[10px] font-bold text-slate-500 hover:bg-slate-50"
        >
          <ArrowLeftIcon size={13} /> Liste
        </button>
      </header>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => {
            if (!dateError) onSave(values as Education);
          })}
          className="grid gap-3.5 sm:grid-cols-2"
        >
          <FormField
            name="institution"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Établissement
                </FormLabel>
                <FormControl>
                  <FreeTextInput
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Nom de l’établissement"
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="studyType"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">Diplôme</FormLabel>
                <FormControl>
                  <SmartCombobox
                    value={field.value ?? ""}
                    options={DEGREE_OPTIONS}
                    placeholder="Ex. Licence"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="area"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Domaine d’études
                </FormLabel>
                <FormControl>
                  <SmartCombobox
                    value={field.value ?? ""}
                    options={STUDY_AREA_OPTIONS}
                    placeholder="Ex. Informatique"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="score"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Mention ou résultat{" "}
                  <span className="ml-1 text-[8px] font-medium text-slate-400">Facultatif</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Ex. Mention Bien"
                    className="min-h-10 rounded-xl bg-white text-xs"
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <div className="sm:col-span-2">
            <DateField
              startDate={start}
              endDate={end}
              isPresent={present}
              presentLabel="Formation en cours"
              onStartDateChange={(value) =>
                form.setValue("startDate" as keyof Values, value as never)
              }
              onEndDateChange={(value) => form.setValue("endDate" as keyof Values, value as never)}
              onPresentChange={(value) =>
                form.setValue("isPresent" as keyof Values, value as never)
              }
              onError={onDateError}
            />
            {dateError && (
              <p className="mt-1 text-[10px] text-rose-600">Vérifiez la période renseignée.</p>
            )}
          </div>
          <FormField
            name="summary"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <div className="flex justify-between">
                  <FormLabel className="text-[11px] font-bold text-indigo-950">
                    Description{" "}
                    <span className="ml-1 text-[8px] font-medium text-slate-400">Facultatif</span>
                  </FormLabel>
                  <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-violet-600">
                    <SparkleIcon size={11} weight="fill" /> Assistance IA
                  </span>
                </div>
                <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={field.onChange}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        onChange={(value) => {
                          editor.commands.setContent(value, true);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 sm:col-span-2">
            <button
              type="button"
              onClick={onCancel}
              className="min-h-10 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={dateError}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800 disabled:opacity-40"
            >
              <CheckIcon size={14} weight="bold" />
              {item ? "Enregistrer" : "Ajouter"}
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};
